import { ChevronDown, ChevronLeft, ChevronRight, MessageCircle, X } from 'lucide-react';
import { useCallback, useRef, useMemo, useReducer, useEffect } from 'react';
import useValidatePhone from '@/services/useValidatePhone';
import { isValidPhoneNumber } from '@/validators';
import { useRouter } from '@tanstack/react-router';
import { PhoneForm, FeedbackForm } from './components';

import { convertPhoneToUKFormat } from '@/utils/helpers';
import { handleZohoUpsert, triggerDiscordMessage } from '@/utils/functions';
import { DISCORD_BOTS } from '@/services/constants';
import { loadFromStorage, saveToStorage } from '@/utils/client-storage/localStorage';
import { useClaimUpsert } from '@/services/useClaimUpsert';
import { toast } from '@/utils/toast';

interface CommonIssue {
  readonly id: string;
  readonly title: string;
  readonly solution?: React.ReactNode;
}

interface DialogState {
  isOpen: boolean;
  selectedIssue: CommonIssue | null;
  isSubmitting: boolean;
}

interface ValidationState {
  value: string;
  error: boolean;
  validated: boolean;
  errorMessage?: string;
}

interface FormState {
  phone: ValidationState;
  feedback: ValidationState;
}

const CONSTANTS = {
  PHONE_VALIDATION_DELAY: 1000,
  REQUIRED_PHONE_DIGITS: 10 | 11,
  DIALOG_Z_INDEX: 1200,
  BUTTON_Z_INDEX: 1100,
} as const;

type DialogAction =
  | { type: 'OPEN_DIALOG' }
  | { type: 'CLOSE_DIALOG' }
  | { type: 'SELECT_ISSUE'; payload: CommonIssue }
  | { type: 'GO_BACK' }
  | { type: 'SET_SUBMITTING'; payload: boolean };

type FormAction =
  | { type: 'SET_PHONE'; payload: Partial<ValidationState> }
  | { type: 'SET_FEEDBACK'; payload: Partial<ValidationState> }
  | { type: 'RESET_FORMS' };

const dialogReducer = (state: DialogState, action: DialogAction): DialogState => {
  switch (action.type) {
    case 'OPEN_DIALOG':
      return { ...state, isOpen: true };
    case 'CLOSE_DIALOG':
      return { isOpen: false, selectedIssue: null, isSubmitting: false };
    case 'SELECT_ISSUE':
      return { ...state, selectedIssue: action.payload };
    case 'GO_BACK':
      return { ...state, selectedIssue: null };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };
    default:
      return state;
  }
};

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'SET_PHONE':
      return { ...state, phone: { ...state.phone, ...action.payload } };
    case 'SET_FEEDBACK':
      return { ...state, feedback: { ...state.feedback, ...action.payload } };
    case 'RESET_FORMS':
      return {
        phone: { value: '', error: false, validated: false, errorMessage: undefined },
        feedback: { value: '', error: false, validated: false },
      };
    default:
      return state;
  }
};

const usePhoneValidation = (onValidationChange: (state: Partial<ValidationState>) => void) => {
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isValidatingRef = useRef<boolean>(false);
  const phoneMutation = useValidatePhone();

  const validatePhone = useCallback(
    async (phoneValue: string): Promise<boolean> => {
      if (isValidatingRef.current) return false;

      try {
        isValidatingRef.current = true;
        const trimmedPhone = phoneValue.trim();

        if (!trimmedPhone || !isValidPhoneNumber(trimmedPhone)) {
          onValidationChange({
            error: true,
            validated: false,
            errorMessage: 'Please enter a valid mobile number',
          });
          return false;
        }

        const isValid = await phoneMutation.mutateAsync(trimmedPhone);

        if (!isValid || phoneMutation.isError) {
          onValidationChange({
            error: true,
            validated: false,
            errorMessage: 'Please enter a valid mobile number',
          });
          return false;
        }

        onValidationChange({ error: false, validated: true, errorMessage: undefined });
        return true;
      } catch (err) {
        onValidationChange({
          error: true,
          validated: false,
          errorMessage: 'Please enter a valid mobile number',
        });
        console.error('Phone validation error:', err);
        return false;
      } finally {
        isValidatingRef.current = false;
      }
    },
    [phoneMutation, onValidationChange]
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      const numericValue = value.replace(/\D/g, '');
      const numericLength = numericValue.length;

      onValidationChange({ value, error: false, validated: false, errorMessage: undefined });

      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }

      if (numericLength > 0) {
        validationTimeoutRef.current = setTimeout(() => {
          if (numericLength < 10) {
            onValidationChange({
              value,
              error: true,
              validated: false,
              errorMessage: 'Incomplete number',
            });
          } else if (numericLength === 10 || numericLength === 11) {
            validatePhone(value);
          }
        }, CONSTANTS.PHONE_VALIDATION_DELAY);
      }
    },
    [validatePhone, onValidationChange]
  );

  const cleanup = useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    isValidatingRef.current = false;
  }, []);

  return {
    validatePhone,
    handlePhoneChange,
    cleanup,
    isValidating: phoneMutation.isPending && isValidatingRef.current,
  };
};

const useSubmission = (onSuccess: () => void, onError: (error: string) => void) => {
  const claimUpsertMutation = useClaimUpsert();

  const sendFeedbackToZoho = useCallback((description: string) => {
    const lSInfo = loadFromStorage('userAnswers');
    const claimNumber = loadFromStorage('currentId');
    if (lSInfo?.basicInfo?.yourEmail) {
      fetch('https://v2be.fair.inc/zoho-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: `${lSInfo?.basicInfo?.yourEmail}`,
          feedback: description,
          name: `${lSInfo?.basicInfo?.givenName} ${lSInfo?.basicInfo?.surname}${
            claimNumber ? ` - [${claimNumber}]` : ''
          }`,
          contact: `${lSInfo?.basicInfo?.yourPhone}`,
        }),
      });
    }
  }, []);

  const submitPhoneUpdate = useCallback(
    async (phone: string): Promise<void> => {
      try {
        const claimNumber = loadFromStorage('currentId');
        const lSInfo = loadFromStorage('userAnswers');

        if (lSInfo && lSInfo.basicInfo) {
          saveToStorage('userAnswers', {
            basicInfo: {
              ...lSInfo.basicInfo,
              yourPhone: convertPhoneToUKFormat(phone),
              phone_number: convertPhoneToUKFormat(phone),
            },
          });
        } else {
          saveToStorage('userAnswers', {
            basicInfo: {
              yourPhone: convertPhoneToUKFormat(phone),
              phone_number: convertPhoneToUKFormat(phone),
            },
          });
        }
        saveToStorage('otp_phone_number', convertPhoneToUKFormat(phone));

        await handleZohoUpsert({
          Phone: phone,
        });

        if (window.location.pathname.includes('/submit-claim')) {
          const idvUrlSMS = `${import.meta.env.VITE_BACKEND_API_BASE_URL || 'http://localhost:8080'}/sms`;
          const sendSMSPayload = {
            phoneNumber: convertPhoneToUKFormat(phone),
            idvLink: `https://www.fair.inc/get-started/id-selfie-upload?id=${claimNumber}&chase_source=sms`,
            smsType: 'idv',
          };

          fetch(idvUrlSMS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(sendSMSPayload),
            credentials: 'include',
          }).catch((e) => {
            console.error('ERROR sending IDV SMS', e);
          });

          setTimeout(() => {
            handleZohoUpsert(
              {
                Changed_Phone_No: true,
              },
              'contact'
            );
          }, 1000);
        }

        const response = await claimUpsertMutation.mutateAsync({
          phone_number: phone,
          feedback_action: `Update My Phone Number (${phone})`,
        });

        if (response.statusCode === 200) {
          toast.success('Phone number updated successfully');
        } else {
          toast.error('Failed to update phone number');
        }

        triggerDiscordMessage({
          bot: DISCORD_BOTS.FEEDBACK,
          content: `# Claim Number: ${claimNumber}
                  - Name: **${lSInfo?.basicInfo?.givenName} ${lSInfo?.basicInfo?.surname}** 
                  - **Issue Action:** Update My Phone Number
                  - **Value:** ${phone}
                `,
        });

        onSuccess();
      } catch (error) {
        console.error('Failed to update phone number:', error);
        onError('Failed to update phone number');
      }
    },
    [claimUpsertMutation, onSuccess, onError]
  );

  const submitFeedback = useCallback(
    async (feedback: string): Promise<void> => {
      try {
        const claimNumber = loadFromStorage('currentId');
        const lSInfo = loadFromStorage('userAnswers');

        triggerDiscordMessage({
          bot: DISCORD_BOTS.FEEDBACK,
          content: `# Claim Number: ${claimNumber}
                  - Name: **${lSInfo?.basicInfo?.givenName} ${lSInfo?.basicInfo?.surname}**
            - **Feedback:** ${feedback}
            `,
        });

        const response = await claimUpsertMutation.mutateAsync({
          feedback_submitted: true,
          feedback: feedback,
        });

        if (response.statusCode === 200) {
          toast.success('Your problem has been reported successfully');
        } else {
          toast.error('Failed to report your problem');
        }

        sendFeedbackToZoho(feedback);

        onSuccess();
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        onError('Failed to submit feedback');
      }
    },
    [sendFeedbackToZoho, claimUpsertMutation, onSuccess, onError]
  );

  return { submitPhoneUpdate, submitFeedback };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function useFeedbackDialog() {
  const [dialogState, dispatchDialog] = useReducer(dialogReducer, {
    isOpen: false,
    selectedIssue: null,
    isSubmitting: false,
  });

  const [formState, dispatchForm] = useReducer(formReducer, {
    phone: { value: '', error: false, validated: false },
    feedback: { value: '', error: false, validated: false },
  });

  const router = useRouter();
  const isHome = useMemo(
    () => router.state.location?.pathname === '/',
    [router.state.location?.pathname]
  );

  const commonIssues = useMemo<readonly CommonIssue[]>(
    () =>
      [
        {
          id: 'will-it-cost-me-anything',
          title: 'Will it cost me anything?',
          solution: (
            <div className="p-4">
              <p className="text-gray-700 mb-3">
                <span className="font-bold underline">No Win No Fee!</span>
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our service charge is up to a maximum capped amount of 15-30% plus VAT of the total
                amount we recover for you, and if your claim is unsuccessful you won't have to pay a
                penny. However, if you cancel our agreement after the 14 day cooling off period, a
                cancellation fee of £23 per hour is payable (up to a maximum of 9.5 hours).
              </p>
            </div>
          ),
        },
        {
          id: 'i-cant-remember-my-lender',
          title: "I can't remember my lender",
          solution: (
            <div className="p-4">
              <p className="text-gray-700 leading-relaxed">
                Don't worry, all you need to do is complete a short online form and we can find your
                vehicle finance lenders from the past 12 years!
              </p>
            </div>
          ),
        },
        {
          id: 'change-number-feedback',
          title: 'Update My Phone Number',
        },
        {
          id: 'report-technical-problem',
          title: 'Report a Technical Problem',
        },
      ] as const,
    []
  );

  const handlePhoneValidationChange = useCallback((state: Partial<ValidationState>) => {
    dispatchForm({ type: 'SET_PHONE', payload: state });
  }, []);

  const { validatePhone, handlePhoneChange, cleanup, isValidating } = usePhoneValidation(
    handlePhoneValidationChange
  );

  const handleSubmissionSuccess = useCallback(() => {
    dispatchForm({ type: 'RESET_FORMS' });
    dispatchDialog({ type: 'CLOSE_DIALOG' });
  }, []);

  const handleSubmissionError = useCallback((error: string) => {
    console.error(error);
  }, []);

  const { submitPhoneUpdate, submitFeedback } = useSubmission(
    handleSubmissionSuccess,
    handleSubmissionError
  );

  const handlePhoneInputChange = useCallback(
    (value: string) => {
      handlePhoneChange(value);
    },
    [handlePhoneChange]
  );

  const handleFeedbackInputChange = useCallback((value: string) => {
    dispatchForm({ type: 'SET_FEEDBACK', payload: { value, error: false } });
  }, []);

  const handlePhoneSubmit = useCallback(async () => {
    if (!formState.phone.value.trim()) {
      dispatchForm({ type: 'SET_PHONE', payload: { error: true } });
      return;
    }

    try {
      dispatchDialog({ type: 'SET_SUBMITTING', payload: true });
      const isValid = await validatePhone(formState.phone.value);
      if (!isValid) return;
      await submitPhoneUpdate(formState.phone.value);
    } finally {
      dispatchDialog({ type: 'SET_SUBMITTING', payload: false });
    }
  }, [formState.phone.value, validatePhone, submitPhoneUpdate]);

  const handleFeedbackSubmit = useCallback(async () => {
    if (!formState.feedback.value.trim()) {
      dispatchForm({ type: 'SET_FEEDBACK', payload: { error: true } });
      return;
    }

    try {
      dispatchDialog({ type: 'SET_SUBMITTING', payload: true });
      await submitFeedback(formState.feedback.value);
    } finally {
      dispatchDialog({ type: 'SET_SUBMITTING', payload: false });
    }
  }, [formState.feedback.value, submitFeedback]);

  const handleDialogClose = useCallback(() => {
    cleanup();
    dispatchForm({ type: 'RESET_FORMS' });
    dispatchDialog({ type: 'CLOSE_DIALOG' });
  }, [cleanup]);

  const handleIssueSelect = useCallback((issue: CommonIssue) => {
    dispatchForm({ type: 'RESET_FORMS' });
    dispatchDialog({ type: 'SELECT_ISSUE', payload: issue });
  }, []);

  const handleGoBack = useCallback(() => {
    dispatchDialog({ type: 'GO_BACK' });
  }, []);

  const handleOpenDialog = useCallback(() => {
    dispatchDialog({ type: 'OPEN_DIALOG' });
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const renderIssueContent = useCallback(() => {
    if (!dialogState.selectedIssue) return null;

    switch (dialogState.selectedIssue.id) {
      case 'change-number-feedback':
        return (
          <PhoneForm
            phone={formState.phone}
            isSubmitting={dialogState.isSubmitting}
            onPhoneChange={handlePhoneInputChange}
            onSubmit={handlePhoneSubmit}
            isValidating={isValidating}
          />
        );
      case 'report-technical-problem':
        return (
          <FeedbackForm
            feedback={formState.feedback}
            isSubmitting={dialogState.isSubmitting}
            onFeedbackChange={handleFeedbackInputChange}
            onSubmit={handleFeedbackSubmit}
          />
        );
      default:
        return dialogState.selectedIssue.solution;
    }
  }, [
    dialogState.selectedIssue,
    dialogState.isSubmitting,
    formState.phone,
    formState.feedback,
    handlePhoneInputChange,
    handlePhoneSubmit,
    handleFeedbackInputChange,
    handleFeedbackSubmit,
    isValidating,
  ]);

  const FeedbackOverlay = useCallback(() => {
    if (!dialogState.isOpen) return null;

    return (
      <div
        className="fixed inset-0 overflow-y-auto backdrop-brightness-75"
        style={{ zIndex: CONSTANTS.DIALOG_Z_INDEX }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
      >
        <div className="flex flex-col items-center md:items-end justify-start md:justify-end h-screen p-5 pt-25 md:pt-5 md:pb-20 md:pr-8">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-auto">
            <div className="bg-primary-dark px-4 py-2 text-white rounded-t-lg flex justify-between items-center">
              <h2 id="dialog-title" className="text-subtitle font-semibold">
                {dialogState.selectedIssue?.title || 'How Can We Help?'}
              </h2>
              <button
                onClick={handleDialogClose}
                className="p-2 flex justify-center items-center bg-white rounded-xl hover:bg-gray-50 transition-colors"
                aria-label="Close dialog"
              >
                <X className="text-black text-body" />
              </button>
            </div>

            <div className="p-4 rounded-b-lg">
              {dialogState.selectedIssue ? (
                <div className="space-y-5">
                  <button
                    className="flex justify-start items-center gap-2 p-3 rounded-lg border w-full hover:bg-primary hover:text-white transition-colors"
                    onClick={handleGoBack}
                    aria-label="Go back to issue list"
                  >
                    <ChevronLeft aria-hidden="true" /> Back
                  </button>

                  {renderIssueContent()}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {commonIssues.map((issue) => (
                    <button
                      key={issue.id}
                      className="flex justify-between items-center gap-2 p-3 rounded-lg border w-full hover:bg-primary hover:text-white transition-colors"
                      onClick={() => handleIssueSelect(issue)}
                      id={issue.id}
                    >
                      <span className="text-left">
                        {issue.title
                          .split(' ')
                          .map((word) => word[0].toUpperCase() + word.slice(1))
                          .join(' ')}
                      </span>
                      <ChevronRight aria-hidden="true" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    dialogState.isOpen,
    dialogState.selectedIssue,
    commonIssues,
    handleDialogClose,
    handleGoBack,
    handleIssueSelect,
    renderIssueContent,
  ]);

  const FeedbackButton = useCallback(
    () => (
      <div
        className="md:fixed md:bottom-8 md:right-8 w-fit"
        style={{ zIndex: CONSTANTS.BUTTON_Z_INDEX }}
      >
        <button
          onClick={handleOpenDialog}
          id="having_issues_button"
          className="bg-white border border-[#E5E7EB] rounded-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
          aria-label="Open help dialog"
        >
          <div className="flex items-center justify-center text-primary">
            <MessageCircle size={20} className="stroke-[1.5]" />
          </div>

          <div className="flex flex-col items-start">
            <span className="text-body md:text-body font-bold text-[#111827] leading-tight">
              Need help?
            </span>
          </div>

          <div className="flex items-center justify-center text-[#4B5563]">
            <ChevronDown size={16} />
          </div>
        </button>
      </div>
    ),
    [handleOpenDialog]
  );

  return {
    FeedbackOverlay: isHome ? () => null : FeedbackOverlay,
    FeedbackButton: isHome ? () => null : FeedbackButton,
  };
}
